import { DotLottieReact } from '@lottiefiles/dotlottie-react';
import { Button, Stack, Typography, useTheme } from '@mui/material';
import { createFileRoute, useNavigate } from '@tanstack/react-router';

import joinStep1Lottie from 'src/assets/animations/join-step1-mobile.lottie?url';
import AccountCreationGraphic from 'src/assets/images/account-creation.svg?react';
import FullBleedHero from 'src/components/FullBleedHero';
import PageFooter from 'src/components/PageFooter';
import { TeamRoles } from 'src/constants/TeamRoles';
import { useOnboardingWorkflow } from 'src/contexts/OnboardingWorkflowContext';
import useResponsive from 'src/hooks/useResponsive';
import { useTeamMemberData } from 'src/hooks/useTeamMemberData';

export const Route = createFileRoute('/(app)/onboarding/account-created')({
    component: AccountCreated,
});

function AccountCreated() {
    const navigate = useNavigate();
    const { isMobile } = useResponsive();
    const { palette } = useTheme();

    const { currentUser } = useTeamMemberData();
    const { role, isTeamAdmin } = currentUser || {};

    const { nextStep, skipToEnd, isProcessing } = useOnboardingWorkflow();

    const shouldShowInviteCta =
        isTeamAdmin || role === TeamRoles.CareRecipient || role === TeamRoles.TeamAdmin;

    return shouldShowInviteCta ? (
        <>
            <Stack sx={{ alignItems: 'center' }}>
                <AccountCreationGraphic width="100%" />
            </Stack>
            <Stack sx={{ gap: 2.5, mb: 2.5 }}>
                <Typography variant="h1" sx={{ fontWeight: 'bold', whiteSpace: 'balance' }}>
                    Your account is created.
                </Typography>

                <Typography variant="body1">
                    Who do you want to keep in the loop? Add a team member to invite family,
                    friends, aides, or other caregivers you want to share information with.
                </Typography>
            </Stack>

            <PageFooter>
                <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    loading={isProcessing}
                    onClick={nextStep}
                >
                    Add a Team Member
                </Button>
                <Button variant="text" fullWidth onClick={skipToEnd} disabled={isProcessing}>
                    Skip for now
                </Button>
            </PageFooter>
        </>
    ) : (
        <Stack
            sx={{
                height: isMobile ? '100svh' : 'auto',
                pb: 2.5,
                justifyContent: 'space-between',
                gap: 2,
            }}
        >
            <FullBleedHero gradientColor={palette.common.white}>
                <DotLottieReact src={joinStep1Lottie} autoplay layout={{ fit: 'fit-width' }} />
            </FullBleedHero>

            <Stack sx={{ gap: 2.5, pb: isMobile ? 2.5 : 0 }}>
                <Typography variant="h1" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                    Congratulations! You are ready to use Sage Home.
                </Typography>
            </Stack>

            <Button
                variant="contained"
                color="primary"
                fullWidth
                loading={isProcessing}
                onClick={() => navigate({ to: '/home' })}
            >
                Go to Homepage
            </Button>
        </Stack>
    );
}
